'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { SchemaReview } from '@/components/SchemaReview';
import { ResultShareButton } from '@/components/ResultShareButton';
import { toPng } from 'dom-to-image';

interface ResultCardProps {
  result: Result;
  imageUrl?: string;
  onAnalyzeAnother?: () => void;
}

// 辅助函数：绘制圆角矩形
const drawRoundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) => {
  const path = new Path2D();
  path.moveTo(x + radius, y);
  path.lineTo(x + width - radius, y);
  path.quadraticCurveTo(x + width, y, x + width, y + radius);
  path.lineTo(x + width, y + height - radius);
  path.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  path.lineTo(x + radius, y + height);
  path.quadraticCurveTo(x, y + height, x, y + height - radius);
  path.lineTo(x, y + radius);
  path.quadraticCurveTo(x, y, x + radius, y);
  path.closePath();
  return path;
};

export const ResultCard = ({ result, imageUrl, onAnalyzeAnother }: ResultCardProps) => {
  const [randomPercentage, setRandomPercentage] = useState(95);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isGiftOpened, setIsGiftOpened] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    // 生成92到98之间的随机数
    const randomNum = Math.floor(Math.random() * (98 - 92 + 1)) + 92;
    setRandomPercentage(randomNum);
  }, [result]);

  if (!result || !result.score) {
    return null;
  }

  const displayScore = result.score;

  // 获取激励文案
  const getEncouragementMessage = (score: number): string => {
    if (score <= 2.0) {
      return "True beauty radiates from within - your kindness, intelligence, and unique spirit are what make you truly special.";
    } else if (score <= 3.0) {
      return "Beauty is not about perfection, it's about authenticity. Your genuine smile and warm heart are your greatest assets.";
    } else if (score <= 4.0) {
      return "Every face tells a unique story. Your individuality and character shine brighter than any conventional standard.";
    } else if (score <= 5.0) {
      return "Confidence is the most attractive quality anyone can have. Embrace your unique features - they make you wonderfully you.";
    }
    return "";
  };

  // 获取皮肤状态中分数最高的项目
  const getBestSkinStatus = () => {
    if (!result.skin_status) {
      return {
        type: 'health',
        label: 'Skin Health',
        score: 0,
        description: 'No data available'
      };
    }

    // Convert skin status score from percentage to 10-point scale
    const convertedScore = result.skin_status.score ? result.skin_status.score / 10 : 0;

    // Return corresponding English labels based on state type
    const getStateLabel = (state: string) => {
      const stateMap: { [key: string]: string } = {
        health: 'Health',
        stain: 'Pigmentation',
        acne: 'Acne',
        dark_circle: 'Dark Circles'
      };
      return stateMap[state] || 'Skin Condition';
    };

    return {
      type: result.skin_status.state || 'health',
      label: getStateLabel(result.skin_status.state || 'health'),
      score: convertedScore,
      description: result.skin_status.description || 'No data available'
    };
  };

  const bestSkinStatus = getBestSkinStatus();

  return (
    <>
      <SchemaReview score={displayScore} gender={result.gender} age={result.age} />
      <div className="bg-gradient-to-r from-violet-50 to-pink-50 p-6 rounded-3xl shadow-lg max-w-5xl mx-auto relative overflow-hidden">
        <div className="flex flex-col items-center">
          {imageUrl && (
            <div className="w-32 sm:w-40 h-32 sm:h-40 rounded-full overflow-hidden border-4 border-white shadow-lg mb-2 sm:mb-4" style={{ minHeight: '160px' }}>
              <img
                src={imageUrl}
                alt="Your photo"
                className="w-full h-full object-cover"
                width={160}
                height={160}
                loading="eager"
                style={{ aspectRatio: '1/1' }}
              />
            </div>
          )}
          
          <div className="w-full space-y-4 sm:space-y-6">
            <div className="text-center">
              <h2 className="text-xl sm:text-2xl font-bold text-violet-600">
                Your Beauty Analysis
              </h2>
              {result.score && (
                <div className="mt-3 sm:mt-4 text-center bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
                  <div className="flex items-baseline justify-center">
                    <span className="text-5xl sm:text-7xl font-bold text-violet-600">{result.score.toFixed(1)}</span>
                    <span className="text-lg sm:text-xl text-gray-500 ml-2">/10</span>
                  </div>
                  {result.tips && (
                    <p className="mt-2 text-sm sm:text-base text-gray-600">{result.tips}</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* Smile Index */}
                {result.smile?.isSmiling && (
                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4">
                    <h4 className="text-sm sm:text-base font-semibold text-gray-800 mb-2 flex items-center justify-between">
                      <div className="flex items-center">
                        <span>Smile Index</span>
                        <span className="ml-1 sm:ml-2 text-lg sm:text-xl">😊</span>
                      </div>
                      <span className="text-sm sm:text-base font-medium text-violet-600">{(result.smile.value / 100 * 10).toFixed(1)}/10</span>
                    </h4>
                    <div className="space-y-1 sm:space-y-2">
                      <div className="relative pt-1">
                        <div className="overflow-hidden h-1.5 sm:h-2 text-xs flex rounded bg-violet-100">
                          <div
                            style={{ width: `${result.smile.value}%` }}
                            className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-violet-500"
                          ></div>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm sm:text-base font-medium text-violet-600">{result.smile.intensity}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Attraction Score */}
                <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4">
                  <h4 className="text-sm sm:text-base font-semibold text-gray-800 mb-2 flex items-center justify-between">
                    <div className="flex items-center">
                      <span>Opposite-Sex Attractiveness Score</span>
                      <span className="ml-1 sm:ml-2 text-lg sm:text-xl">💝</span>
                    </div>
                    <span className="text-sm sm:text-base font-medium text-violet-600">{result.attraction_score.toFixed(1)}/10</span>
                  </h4>
                  <div className="space-y-1 sm:space-y-2">
                    <div className="relative pt-1">
                      <div className="overflow-hidden h-1.5 sm:h-2 text-xs flex rounded bg-violet-100">
                        <div
                          style={{ width: `${result.attraction_score * 10}%` }}
                          className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-violet-500"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Skin Status */}
                <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4">
                  <h4 className="text-sm sm:text-base font-semibold text-gray-800 mb-2 flex items-center justify-between">
                    <div className="flex items-center">
                      <span>Skin Status</span>
                      <span className="ml-1 sm:ml-2 text-lg sm:text-xl">✨</span>
                    </div>
                    <span className="text-sm sm:text-base font-medium text-violet-600">{bestSkinStatus.score.toFixed(1)}/10</span>
                  </h4>
                  <div className="space-y-1 sm:space-y-2">
                    <div className="relative pt-1">
                      <div className="overflow-hidden h-1.5 sm:h-2 text-xs flex rounded bg-violet-100">
                        <div
                          style={{ width: `${bestSkinStatus.score * 10}%` }}
                          className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-violet-500"
                        ></div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm sm:text-base font-medium text-violet-600">{bestSkinStatus.label}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm sm:text-base font-medium text-violet-600">{bestSkinStatus.description}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4">
                  <h4 className="text-sm sm:text-base font-semibold text-gray-800 mb-2 flex items-center justify-between">
                    <div className="flex items-center">
                      <span>Physical Features</span>
                      <span className="ml-1 sm:ml-2 text-lg sm:text-xl">{result.gender?.toLowerCase() === 'female' ? '👩‍💼' : '👨‍💼'}</span>
                    </div>
                  </h4>
                  <div className="space-y-1 sm:space-y-2">
                    {result.gender && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm sm:text-base">Gender</span>
                        <span className="text-sm sm:text-base font-medium text-violet-600 capitalize">{result.gender}</span>
                      </div>
                    )}
                    {result.age && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm sm:text-base">Age</span>
                        <span className="text-sm sm:text-base font-medium text-violet-600">{result.age}</span>
                      </div>
                    )}
                    {result.emotion && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm sm:text-base">Emotion</span>
                        <span className="text-sm sm:text-base font-medium text-violet-600 capitalize">{result.emotion}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Encouragement Section - Shown when score is 5 or below */}
      {result.score <= 5 && (
        <div className="mt-8 p-6 sm:p-8 bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl shadow-lg border border-pink-100">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-r from-pink-400 to-rose-400 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-2xl">💖</span>
              </div>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
              Remember What Truly Matters
            </h3>
            <div className="max-w-2xl mx-auto">
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed italic">
                "{getEncouragementMessage(result.score)}"
              </p>
            </div>
            <div className="mt-6 flex justify-center">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>✨</span>
                <span>Your worth isn't defined by numbers</span>
                <span>✨</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bonus Section - Shown when score is above 5 */}
      {result.score > 5 && (
        <div className="mt-8 p-8 bg-gradient-to-r from-violet-50 to-pink-50 rounded-2xl shadow-lg">
          <div className="relative">
            <div className="relative">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center justify-center sm:justify-start">
                <span className="text-2xl sm:text-3xl mr-2 transform hover:scale-110 transition-transform duration-200 inline-flex items-center">🎁</span>
                <span className="relative inline-block">
                  Special Gift for You
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-violet-400 to-pink-400 transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100"></span>
                </span>
              </h3>
              
              <div className="text-center mb-4">
                <div className="inline-block relative">
                  <span className="text-lg text-transparent bg-clip-text bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 font-semibold">
                    Congratulations! You're in the Top {randomPercentage}%
                  </span>
                </div>
              </div>

              <div className="flex flex-col items-center space-y-6">
                {!isGiftOpened ? (
                  <button
                    className="group relative px-8 py-4 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl font-medium
                             transform transition-all duration-300 hover:scale-105 hover:shadow-xl
                             focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-opacity-50"
                    onClick={async () => {
                      setIsGiftOpened(true);
                      setIsGenerating(true);
                      try {
                        const canvas = document.createElement('canvas');
                        const width = 720;
                        const height = 480;
                        canvas.width = width;
                        canvas.height = height;
                        const ctx = canvas.getContext('2d');
                        if (!ctx) {
                          console.error('Failed to get canvas context');
                          return;
                        }

                        // 设置背景渐变
                        const bgGradient = ctx.createLinearGradient(0, 0, width, height);
                        bgGradient.addColorStop(0, '#f0f0ff');
                        bgGradient.addColorStop(1, '#fff0f9');
                        ctx.fillStyle = bgGradient;
                        ctx.fillRect(0, 0, width, height);

                        // 左侧面板宽度
                        const leftPanelWidth = width * 0.32;

                        // 绘制左侧面板
                        ctx.save();
                        ctx.fillStyle = '#ffffff';
                        ctx.shadowColor = 'rgba(0, 0, 0, 0.08)';
                        ctx.shadowBlur = 15;
                        ctx.shadowOffsetX = 0;
                        ctx.shadowOffsetY = 2;
                        const leftPanelPath = drawRoundedRect(ctx, 30, 30, leftPanelWidth, height - 60, 16);
                        ctx.fill(leftPanelPath);
                        ctx.restore();

                        // 绘制总分
                        const scoreY = 120;
                        const scoreX = (leftPanelWidth + 60) * 0.5;
                        const circleRadius = 70;
                        const innerRadius = circleRadius - 10;
                        
                        // 计算进度
                        const score = result.score;
                        const progress = (score / 10) * Math.PI * 2;
                        
                        // 绘制背景圆环
                        ctx.beginPath();
                        ctx.arc(scoreX, scoreY, innerRadius, 0, Math.PI * 2);
                        ctx.strokeStyle = 'rgba(124, 58, 237, 0.15)';
                        ctx.lineWidth = 10;
                        ctx.stroke();
                        
                        // 绘制进度圆环
                        const gradientCircle = ctx.createLinearGradient(scoreX - innerRadius, scoreY, scoreX + innerRadius, scoreY);
                        gradientCircle.addColorStop(0, '#7c3aed');
                        gradientCircle.addColorStop(1, '#c084fc');
                        
                        ctx.beginPath();
                        ctx.arc(scoreX, scoreY, innerRadius, -Math.PI / 2, progress - Math.PI / 2);
                        ctx.strokeStyle = gradientCircle;
                        ctx.lineWidth = 10;
                        ctx.lineCap = 'round';
                        ctx.stroke();

                        // 绘制分数
                        ctx.font = 'bold 48px Arial';
                        ctx.fillStyle = '#7c3aed';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(score.toFixed(1), scoreX, scoreY);

                        // 绘制三个指标
                        const metrics = [
                          { label: 'Smile Index', value: result.smile?.value || 0 },
                          { label: 'Attractiveness', value: result.attraction_score * 10 || 0 },
                          { label: 'Skin Status', value: bestSkinStatus.score * 10 || 0 }
                        ];

                        let startY = scoreY + 100;
                        const metricsX = 45;
                        const progressBarWidth = leftPanelWidth - 90;

                        metrics.forEach((metric, index) => {
                          const y = startY + index * 60;

                          // 标签
                          ctx.font = 'bold 18px Arial';
                          ctx.fillStyle = '#1e293b';
                          ctx.textAlign = 'left';
                          ctx.fillText(metric.label, metricsX, y);

                          // 进度条背景
                          ctx.fillStyle = '#f3f4f6';
                          const bgBarPath = drawRoundedRect(ctx, metricsX, y + 20, progressBarWidth, 10, 5);
                          ctx.fill(bgBarPath);

                          // 进度条
                          const barGradient = ctx.createLinearGradient(metricsX, y + 20, metricsX + progressBarWidth, y + 30);
                          barGradient.addColorStop(0, '#7c3aed');
                          barGradient.addColorStop(1, '#c084fc');
                          ctx.fillStyle = barGradient;
                          
                          const progress = (metric.value / 100) * progressBarWidth;
                          const progressPath = drawRoundedRect(ctx, metricsX, y + 20, progress, 10, 5);
                          ctx.fill(progressPath);

                          // 数值
                          ctx.font = 'bold 20px Arial';
                          ctx.fillStyle = '#1e293b';
                          ctx.textAlign = 'right';
                          ctx.fillText((metric.value / 10).toFixed(1), metricsX + progressBarWidth + 45, y + 25);
                        });

                        // 添加水印
                        const watermarkText = 'aiattractiveness.com';
                        ctx.font = 'bold 18px Arial';
                        const textWidth = ctx.measureText(watermarkText).width;
                        const watermarkPadding = 12;
                        const watermarkHeight = 36;
                        const watermarkY = height - watermarkHeight - 35;
                        const watermarkWidth = textWidth + (watermarkPadding * 2);
                        const watermarkX = 45;

                        ctx.save();
                        ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
                        ctx.shadowColor = 'rgba(0, 0, 0, 0.05)';
                        ctx.shadowBlur = 8;
                        ctx.shadowOffsetY = 1;
                        const watermarkPath = drawRoundedRect(ctx, watermarkX, watermarkY, watermarkWidth, watermarkHeight, 18);
                        ctx.fill(watermarkPath);
                        ctx.restore();

                        ctx.fillStyle = 'rgba(124, 58, 237, 0.8)';
                        ctx.textAlign = 'left';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(watermarkText, watermarkX + watermarkPadding, watermarkY + (watermarkHeight / 2));

                        // 如果有用户图片，绘制在右侧
                        if (imageUrl) {
                          const img = new Image();
                          img.src = imageUrl;
                          await new Promise((resolve, reject) => {
                            img.onload = resolve;
                            img.onerror = reject;
                          });

                          const targetAspectRatio = 15 / 16;
                          const maxImageWidth = width - leftPanelWidth - 60;
                          const maxImageHeight = height - 60;
                          
                          let imageWidth, imageHeight;
                          if (maxImageWidth / maxImageHeight > targetAspectRatio) {
                            imageHeight = maxImageHeight;
                            imageWidth = imageHeight * targetAspectRatio;
                          } else {
                            imageWidth = maxImageWidth;
                            imageHeight = imageWidth / targetAspectRatio;
                          }
                          
                          const imageX = leftPanelWidth + 50;
                          const imageY = 30 + (maxImageHeight - imageHeight) / 2;

                          // 绘制图片阴影和背景
                          ctx.save();
                          ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
                          ctx.shadowBlur = 15;
                          ctx.shadowOffsetX = 0;
                          ctx.shadowOffsetY = 5;
                          const imgBgPath = drawRoundedRect(ctx, imageX, imageY, imageWidth, imageHeight, 10);
                          ctx.fillStyle = '#ffffff';
                          ctx.fill(imgBgPath);
                          ctx.restore();

                          // 绘制图片
                          ctx.save();
                          const imgPath = drawRoundedRect(ctx, imageX, imageY, imageWidth, imageHeight, 10);
                          ctx.clip(imgPath);
                          ctx.drawImage(img, imageX, imageY, imageWidth, imageHeight);
                          ctx.restore();
                        }

                        const dataUrl = canvas.toDataURL('image/jpeg', 0.95);

                        setPreviewImage(dataUrl);
                      } catch (error) {
                        console.error('Error generating image:', error);
                      } finally {
                        setIsGenerating(false);
                        const container = document.createElement('div');
                        container.style.position = 'fixed';
                        container.style.left = '-9999px';
                        container.style.top = '0';
                        container.style.width = '800px';
                      }
                    }}
                    disabled={isGenerating}
                  >
                    <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-violet-400 to-purple-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-xl"></span>
                    {isGenerating ? (
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Generating your gift...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span>Unwrap Your Gift</span>
                        <span className="text-xl">✨</span>
                      </div>
                    )}
                  </button>
                ) : null}

                {previewImage && (
                  <div className="mt-6 w-full max-w-2xl mx-auto space-y-4">
                    <div className="relative bg-white p-1 rounded-xl shadow-lg">
                      <img 
                        src={previewImage} 
                        alt="Your Beauty Score Card" 
                        className="w-full rounded-xl shadow-xl"
                        width={720}
                        height={480}
                      />
                    </div>
                    <ResultShareButton result={result} imageUrl={imageUrl} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analyze Another Photo Button */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={onAnalyzeAnother}
          className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-medium text-base
                     transform transition-all duration-300 hover:scale-105 hover:shadow-lg hover:from-blue-600 hover:to-purple-600
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50
                     active:scale-95"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <span>Analyze Another Photo</span>
        </button>
      </div>
    </>
  );
};
